(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-urc-slider]";
  const VIEWPORT_SELECTOR = "[data-urc-viewport]";
  const TRACK_SELECTOR = "[data-urc-track]";
  const SLIDE_SELECTOR = "[data-urc-slide]";
  const REAL_SLIDE_SELECTOR = "[data-urc-real]";
  const DOT_SELECTOR = "[data-urc-dot]";
  const MODAL_TRIGGER_SELECTOR = "[data-urc-modal-template]";
  const SECTION_SELECTOR = "[data-urc-section]";
  const MODAL_SELECTOR = "[data-urc-modal]";
  const MODAL_CONTENT_SELECTOR = "[data-urc-modal-content]";
  const MODAL_CLOSE_SELECTOR = "[data-urc-modal-close]";
  const YOUTUBE_SELECTOR = "[data-urc-youtube-url]";

  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const now = () => (window.performance && performance.now ? performance.now() : Date.now());

  const toBool = (value) => String(value) === "true";

  const toNum = (value, fallback) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  const decodeHtml = (value) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(value || "");
    return textarea.value;
  };

  const normalizeYoutubeId = (value) => {
    const raw = decodeHtml(value).trim();
    if (!raw) return "";

    try {
      const url = new URL(raw);
      const host = url.hostname.replace(/^www\./, "");

      if (host === "youtu.be") {
        return (url.pathname.split("/").filter(Boolean)[0] || "").trim();
      }

      if (
        host === "youtube.com" ||
        host === "m.youtube.com" ||
        host === "music.youtube.com" ||
        host === "youtube-nocookie.com"
      ) {
        const watchId = url.searchParams.get("v");
        if (watchId) return watchId.trim();

        const parts = url.pathname.split("/").filter(Boolean);
        const embedIndex = parts.indexOf("embed");
        const shortsIndex = parts.indexOf("shorts");
        const liveIndex = parts.indexOf("live");

        if (embedIndex > -1 && parts[embedIndex + 1]) return parts[embedIndex + 1].trim();
        if (shortsIndex > -1 && parts[shortsIndex + 1]) return parts[shortsIndex + 1].trim();
        if (liveIndex > -1 && parts[liveIndex + 1]) return parts[liveIndex + 1].trim();
      }
    } catch (error) {}

    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/i,
      /(?:youtu\.be\/)([^?\s/]+)/i,
      /(?:youtube\.com\/shorts\/)([^?\s/]+)/i,
      /(?:youtube\.com\/embed\/)([^?\s/]+)/i,
      /(?:youtube\.com\/live\/)([^?\s/]+)/i
    ];

    for (const pattern of patterns) {
      const match = raw.match(pattern);
      if (match && match[1]) return match[1].trim();
    }

    if (/^[a-zA-Z0-9_-]{8,32}$/.test(raw)) {
      return raw;
    }

    return "";
  };

  const buildYoutubeEmbedUrl = (youtubeUrl) => {
    const videoId = normalizeYoutubeId(youtubeUrl);
    if (!videoId) return "";

    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1"
    });

    return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
  };

  const prepareYoutubeIframes = (modal) => {
    if (!modal) return;

    const nodes = Array.from(modal.querySelectorAll(YOUTUBE_SELECTOR));

    nodes.forEach((node) => {
      const rawUrl = node.getAttribute("data-urc-youtube-url") || "";
      const title = node.getAttribute("data-urc-youtube-title") || "YouTube video";
      const embedUrl = buildYoutubeEmbedUrl(rawUrl);
      const iframe = node.querySelector("iframe");

      if (!iframe) return;

      iframe.setAttribute("title", title);

      if (embedUrl) {
        iframe.setAttribute("src", embedUrl);
        node.classList.remove("urc-youtube--empty");
      } else {
        iframe.removeAttribute("src");
        node.classList.add("urc-youtube--empty");

        if (!node.querySelector(".urc-youtube__message")) {
          const message = document.createElement("p");
          message.className = "urc-youtube__message";
          message.textContent = "YouTube URL is invalid.";
          node.appendChild(message);
        }
      }
    });
  };

  const stopYoutubeIframes = (modal) => {
    if (!modal) return;

    Array.from(modal.querySelectorAll("iframe")).forEach((iframe) => {
      iframe.removeAttribute("src");
    });
  };

  const lockScroll = () => {
    if (!document.documentElement.dataset.urcPreviousOverflow) {
      document.documentElement.dataset.urcPreviousOverflow = document.documentElement.style.overflow || "__blank__";
    }

    if (!document.body.dataset.urcPreviousOverflow) {
      document.body.dataset.urcPreviousOverflow = document.body.style.overflow || "__blank__";
    }

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  };

  const unlockScroll = () => {
    const htmlPrev = document.documentElement.dataset.urcPreviousOverflow;
    const bodyPrev = document.body.dataset.urcPreviousOverflow;

    document.documentElement.style.overflow = htmlPrev && htmlPrev !== "__blank__" ? htmlPrev : "";
    document.body.style.overflow = bodyPrev && bodyPrev !== "__blank__" ? bodyPrev : "";

    delete document.documentElement.dataset.urcPreviousOverflow;
    delete document.body.dataset.urcPreviousOverflow;
  };

  const openModalFallback = (trigger) => {
    if (!trigger || typeof trigger.closest !== "function") return false;

    const templateId = trigger.getAttribute("data-urc-modal-template");
    const section = trigger.closest(SECTION_SELECTOR);

    if (!templateId || !section) return false;

    const modal = section.querySelector(MODAL_SELECTOR);
    const content = modal ? modal.querySelector(MODAL_CONTENT_SELECTOR) : null;
    const template = document.getElementById(templateId);

    if (!modal || !content || !template) return false;

    content.innerHTML = "";
    content.appendChild(template.content.cloneNode(true));

    prepareYoutubeIframes(modal);
    lockScroll();

    modal.__urcLastActiveElement = trigger;
    modal.setAttribute("aria-hidden", "false");
    modal.classList.add("is-open");

    const dialog = modal.querySelector(".urc-modal__dialog");
    const title = modal.querySelector(".urc-modal-content__title");

    if (title && dialog) {
      const titleId = title.id || `urc-modal-title-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      title.id = titleId;
      dialog.setAttribute("aria-labelledby", titleId);
    }

    window.requestAnimationFrame(() => {
      const closeButton = modal.querySelector(".urc-modal__close");
      const focusTarget = closeButton || dialog;

      if (focusTarget && typeof focusTarget.focus === "function") {
        focusTarget.focus({ preventScroll: true });
      }
    });

    return true;
  };

  const closeModalFallback = (modal, restoreFocus = true) => {
    if (!modal) return;

    const content = modal.querySelector(MODAL_CONTENT_SELECTOR);

    stopYoutubeIframes(modal);

    modal.setAttribute("aria-hidden", "true");
    modal.classList.remove("is-open");

    if (content) {
      content.innerHTML = "";
    }

    unlockScroll();

    const lastActive = modal.__urcLastActiveElement;
    modal.__urcLastActiveElement = null;

    if (
      restoreFocus &&
      lastActive &&
      typeof lastActive.focus === "function" &&
      document.contains(lastActive)
    ) {
      window.requestAnimationFrame(() => {
        lastActive.focus({ preventScroll: true });
      });
    }
  };

  const ensureModalFallbackEvents = () => {
    if (window.__urcCarouselModalFallbackBound) return;
    window.__urcCarouselModalFallbackBound = true;

    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;

        if (!target || typeof target.closest !== "function") return;

        const closeTrigger = target.closest(MODAL_CLOSE_SELECTOR);

        if (!closeTrigger) return;

        const modal = closeTrigger.closest(MODAL_SELECTOR);

        if (!modal) return;

        event.preventDefault();
        event.stopPropagation();
        closeModalFallback(modal);
      },
      true
    );

    document.addEventListener("keydown", (event) => {
      const modal = document.querySelector(`${MODAL_SELECTOR}.is-open`);

      if (!modal) return;

      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        closeModalFallback(modal);
      }
    });
  };

  const openModalFromTrigger = (trigger) => {
    if (!trigger) return false;

    if (
      window.UGXReviewCarouselModal &&
      typeof window.UGXReviewCarouselModal.openFromTrigger === "function"
    ) {
      const opened = window.UGXReviewCarouselModal.openFromTrigger(trigger);

      if (opened) {
        return true;
      }
    }

    return openModalFallback(trigger);
  };

  class UGXReviewCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector(VIEWPORT_SELECTOR);
      this.track = root.querySelector(TRACK_SELECTOR);
      this.dots = Array.from(root.querySelectorAll(DOT_SELECTOR));

      this.realSlides = [];
      this.slides = [];
      this.realCount = 0;

      this.perView = 1;
      this.gap = 0;
      this.slideW = 0;
      this.step = 0;
      this.cloneCount = 0;
      this.index = 0;
      this.currentTx = 0;
      this.enabled = false;

      this.autoplay = toBool(root.dataset.autoplay);
      this.loop = toBool(root.dataset.loop);
      this.pauseHover = toBool(root.dataset.pauseHover);
      this.delay = toNum(root.dataset.delay, 3500);
      this.speed = toNum(root.dataset.speed, 520);

      this.timer = null;
      this.isPaused = false;
      this.isPointerDown = false;
      this.pointerId = null;

      this.startX = 0;
      this.startY = 0;
      this.startTx = 0;
      this.movedX = 0;
      this.movedY = 0;
      this.movedAbs = 0;
      this.locked = false;
      this.dragStartTime = 0;
      this.clickGuardUntil = 0;
      this.startTrigger = null;

      this.destroyed = false;
      this.resizeRaf = null;

      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onDotClick = this.onDotClick.bind(this);

      this.init();
    }

    init() {
      if (!this.root || !this.viewport || !this.track) return;

      ensureModalFallbackEvents();

      this.root.style.setProperty("--urc-speed", `${this.speed}ms`);
      this.root.style.setProperty("--urc-autoplay-delay", `${this.delay}ms`);

      this.bind();
      this.rebuild();
    }

    bind() {
      this.viewport.addEventListener("pointerdown", this.onPointerDown, { passive: true });
      this.viewport.addEventListener("pointermove", this.onPointerMove, { passive: false });
      this.viewport.addEventListener("pointerup", this.onPointerUp, { passive: false });
      this.viewport.addEventListener("pointercancel", this.onPointerUp, { passive: true });

      this.root.addEventListener("click", this.onClickCapture, true);
      this.track.addEventListener("transitionend", this.onTransitionEnd);

      this.dots.forEach((dot) => dot.addEventListener("click", this.onDotClick));

      if (this.pauseHover) {
        this.root.addEventListener("mouseenter", this.onMouseEnter);
        this.root.addEventListener("mouseleave", this.onMouseLeave);
      }

      window.addEventListener("resize", this.onResize, { passive: true });
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }

    destroy() {
      this.destroyed = true;
      this.stopAutoplay();

      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = null;
      }

      if (this.viewport) {
        this.viewport.removeEventListener("pointerdown", this.onPointerDown);
        this.viewport.removeEventListener("pointermove", this.onPointerMove);
        this.viewport.removeEventListener("pointerup", this.onPointerUp);
        this.viewport.removeEventListener("pointercancel", this.onPointerUp);
      }

      if (this.root) {
        this.root.removeEventListener("click", this.onClickCapture, true);
        this.root.removeEventListener("mouseenter", this.onMouseEnter);
        this.root.removeEventListener("mouseleave", this.onMouseLeave);
      }

      if (this.track) {
        this.track.removeEventListener("transitionend", this.onTransitionEnd);
      }

      this.dots.forEach((dot) => dot.removeEventListener("click", this.onDotClick));

      window.removeEventListener("resize", this.onResize);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    getPerView() {
      if (window.matchMedia("(min-width: 990px)").matches) {
        return toNum(this.root.dataset.perViewDesktop, 5);
      }

      if (window.matchMedia("(min-width: 750px)").matches) {
        return toNum(this.root.dataset.perViewTablet, 3);
      }

      return toNum(this.root.dataset.perViewMobile, 2);
    }

    removeClones() {
      Array.from(this.track.querySelectorAll("[data-urc-clone]")).forEach((clone) => clone.remove());
    }

    rebuild() {
      if (this.destroyed) return;

      this.stopAutoplay();
      this.removeClones();

      this.realSlides = Array.from(this.track.querySelectorAll(REAL_SLIDE_SELECTOR));
      this.realCount = this.realSlides.length;
      this.perView = this.getPerView();

      if (this.realCount <= 0) return;

      this.measure();

      this.enabled = this.realCount > this.perView;

      if (this.loop && this.enabled) {
        this.cloneCount = Math.min(this.realCount, Math.ceil(this.perView) + 2);

        const before = this.realSlides.slice(-this.cloneCount).map((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute("data-urc-clone", "true");
          clone.removeAttribute("data-urc-real");
          return clone;
        });

        const after = this.realSlides.slice(0, this.cloneCount).map((slide) => {
          const clone = slide.cloneNode(true);
          clone.setAttribute("data-urc-clone", "true");
          clone.removeAttribute("data-urc-real");
          return clone;
        });

        before.reverse().forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
        after.forEach((clone) => this.track.appendChild(clone));

        this.index = this.cloneCount;
      } else {
        this.cloneCount = 0;
        this.index = 0;
      }

      this.slides = Array.from(this.track.querySelectorAll(SLIDE_SELECTOR));

      this.measure();
      this.applyTx(this.calcTx(this.index), false);
      this.syncDots(true);

      window.requestAnimationFrame(() => {
        this.startAutoplay();
      });
    }

    measure() {
      const viewportW = this.viewport.getBoundingClientRect().width || 1;
      const styles = window.getComputedStyle(this.track);
      const rawGap = styles.columnGap || styles.gap || "0px";

      this.gap = parseFloat(rawGap) || 0;
      this.slideW = (viewportW - this.gap * (this.perView - 1)) / this.perView;
      this.slideW = Math.max(1, this.slideW);
      this.step = this.slideW + this.gap;

      this.root.style.setProperty("--urc-slide-width", `${this.slideW}px`);
    }

    calcTx(index) {
      return -(index * this.step);
    }

    applyTx(tx, animate = true) {
      if (!this.track) return;

      if (!animate) {
        this.track.style.transition = "none";
        this.track.style.transform = `translate3d(${tx}px, 0, 0)`;
        this.currentTx = tx;

        this.track.offsetHeight;
        this.track.style.transition = "";
        return;
      }

      this.track.style.transition = `transform ${this.speed}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      this.track.style.transform = `translate3d(${tx}px, 0, 0)`;
      this.currentTx = tx;
    }

    realIndex() {
      if (!this.realCount) return 0;

      let idx = this.index - this.cloneCount;
      idx = idx % this.realCount;

      if (idx < 0) idx += this.realCount;

      return idx;
    }

    goToReal(realIndex, animate = true, restart = true) {
      if (!this.enabled) return;

      const safe = clamp(realIndex, 0, this.realCount - 1);
      this.index = this.cloneCount + safe;

      this.applyTx(this.calcTx(this.index), animate);
      this.syncDots(true);

      if (restart) {
        this.restartAutoplay();
      }
    }

    goBy(delta, animate = true, restart = false) {
      if (!this.enabled) return;

      this.index += delta;

      if (!this.loop) {
        const maxIndex = Math.max(0, this.realCount - this.perView);

        if (this.index > maxIndex) {
          this.index = 0;
        }

        if (this.index < 0) {
          this.index = maxIndex;
        }
      }

      this.applyTx(this.calcTx(this.index), animate);
      this.syncDots(true);

      if (restart) {
        this.restartAutoplay();
      }
    }

    onTransitionEnd(event) {
      if (event.target !== this.track || event.propertyName !== "transform") return;
      if (!this.loop || !this.enabled) return;

      const firstReal = this.cloneCount;
      const lastReal = this.cloneCount + this.realCount - 1;

      if (this.index > lastReal) {
        this.index = firstReal + (this.index - lastReal - 1);
        this.applyTx(this.calcTx(this.index), false);
      }

      if (this.index < firstReal) {
        this.index = lastReal - (firstReal - this.index - 1);
        this.applyTx(this.calcTx(this.index), false);
      }

      this.syncDots(false);
    }

    syncDots(resetProgress = false) {
      const active = this.realIndex();

      this.dots.forEach((dot) => {
        const idx = Number(dot.dataset.index);
        const isActive = idx === active;

        dot.classList.toggle("is-active", isActive);

        if (isActive) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }

        if (resetProgress) {
          const bar = dot.querySelector(".urc-dot__bar");

          if (bar) {
            bar.style.animation = "none";
            bar.offsetHeight;
            bar.style.animation = "";
          }
        }
      });
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.autoplay || !this.enabled || this.destroyed) return;
      if (this.isPaused || document.hidden) return;

      this.root.classList.remove("is-paused");

      this.timer = window.setTimeout(() => {
        this.goBy(1, true, false);
        this.startAutoplay();
      }, this.delay);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    onMouseEnter() {
      this.isPaused = true;
      this.root.classList.add("is-paused");
      this.stopAutoplay();
    }

    onMouseLeave() {
      this.isPaused = false;
      this.root.classList.remove("is-paused");
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    onPointerDown(event) {
      if (!this.enabled) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this.isPointerDown = true;
      this.pointerId = event.pointerId;

      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startTx = this.currentTx;

      this.movedX = 0;
      this.movedY = 0;
      this.movedAbs = 0;
      this.locked = false;
      this.dragStartTime = now();

      this.startTrigger =
        event.target && typeof event.target.closest === "function"
          ? event.target.closest(MODAL_TRIGGER_SELECTOR)
          : null;

      if (this.startTrigger && !this.root.contains(this.startTrigger)) {
        this.startTrigger = null;
      }

      this.root.classList.add("is-dragging");
      this.root.classList.add("is-paused");
      this.stopAutoplay();

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {}
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.pointerId !== event.pointerId) return;

      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);

      this.movedX = dx;
      this.movedY = dy;
      this.movedAbs = adx;

      if (!this.locked) {
        if (ady > adx && ady > 10) {
          this.releasePointer(event, true);
          return;
        }

        if (adx > 7) {
          this.locked = true;
        }
      }

      if (this.locked) {
        if (event.cancelable) event.preventDefault();
        this.applyTx(this.startTx + dx, false);
      }
    }

    onPointerUp(event) {
      if (!this.isPointerDown || this.pointerId !== event.pointerId) return;

      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;
      const adx = Math.abs(dx);
      const ady = Math.abs(dy);
      const elapsed = Math.max(1, now() - this.dragStartTime);
      const vx = dx / elapsed;
      const threshold = Math.max(28, this.step * 0.16);
      const wasRealDrag = this.locked && adx > 8;

      const triggerToOpen = !wasRealDrag && adx <= 8 && ady <= 8 ? this.startTrigger : null;

      this.releasePointer(event, true);

      if (triggerToOpen) {
        const opened = openModalFromTrigger(triggerToOpen);

        if (opened) {
          this.clickGuardUntil = Date.now() + 600;

          if (event.cancelable) {
            event.preventDefault();
          }

          event.stopPropagation();
          return;
        }
      }

      if (wasRealDrag) {
        this.clickGuardUntil = Date.now() + 350;
      }

      let delta = 0;

      if (Math.abs(dx) > threshold || Math.abs(vx) > 0.55) {
        delta = dx < 0 ? 1 : -1;
      }

      if (delta !== 0) {
        this.goBy(delta, true, true);
      } else {
        const nearest = Math.round(-this.currentTx / this.step);
        this.index = nearest;

        if (!this.loop) {
          this.index = clamp(this.index, 0, Math.max(0, this.realCount - this.perView));
        }

        this.applyTx(this.calcTx(this.index), true);
        this.syncDots(true);
        this.startAutoplay();
      }
    }

    releasePointer(event, shouldResume) {
      this.isPointerDown = false;
      this.pointerId = null;

      this.root.classList.remove("is-dragging");

      if (!this.isPaused) {
        this.root.classList.remove("is-paused");
      }

      try {
        this.viewport.releasePointerCapture(event.pointerId);
      } catch (error) {}

      if (shouldResume && !this.isPaused) {
        this.startAutoplay();
      }

      this.startTrigger = null;
    }

    onClickCapture(event) {
      if (Date.now() >= this.clickGuardUntil) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    onDotClick(event) {
      const dot = event.currentTarget;
      const idx = Number(dot.dataset.index);

      if (!Number.isFinite(idx)) return;

      this.goToReal(idx, true, true);
    }

    onResize() {
      if (this.destroyed) return;

      if (this.resizeRaf) {
        window.cancelAnimationFrame(this.resizeRaf);
      }

      this.resizeRaf = window.requestAnimationFrame(() => {
        this.resizeRaf = null;
        this.rebuild();
      });
    }
  }

  const mount = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new UGXReviewCarousel(root));
    });

    if (
      window.UGXReviewCarouselModal &&
      typeof window.UGXReviewCarouselModal.mount === "function"
    ) {
      window.UGXReviewCarouselModal.mount(scope);
    }
  };

  const unmount = (scope = document) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });

    if (
      window.UGXReviewCarouselModal &&
      typeof window.UGXReviewCarouselModal.unmount === "function"
    ) {
      window.UGXReviewCarouselModal.unmount(scope);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(document), { once: true });
  } else {
    mount(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!event || !event.target) return;
    mount(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (!event || !event.target) return;
    unmount(event.target);
  });

  document.addEventListener("shopify:section:select", (event) => {
    if (!event || !event.target) return;
    mount(event.target);
  });
})();